import React, { useState } from 'react';
import {
  Activity,
  Search,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Shield,
  FileText,
  Calendar,
  Lock,
  Settings,
  Clock,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { mockAuditLogs } from '@mocks/audit';
import type { MockAuditLog } from '@mocks/audit';

export const AuditLogs: React.FC = () => {
  const [logs] = useState<MockAuditLog[]>(mockAuditLogs);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.details.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.ip_address.includes(searchQuery);

    const matchesCategory = filterCategory === 'all' || log.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || log.status === filterStatus;
    const matchesRole = filterRole === 'all' || log.user_role === filterRole;

    return matchesSearch && matchesCategory && matchesStatus && matchesRole;
  });

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterCategory, filterStatus, filterRole]);

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />} size="sm">Success</Badge>;
      case 'FAILURE':
        return <Badge variant="error" icon={<XCircle className="h-3 w-3" />} size="sm">Failure</Badge>;
      case 'WARNING':
        return <Badge variant="warning" icon={<AlertTriangle className="h-3 w-3" />} size="sm">Warning</Badge>;
      default:
        return <Badge variant="default" size="sm">{status}</Badge>;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'AUTH':
        return <Lock className="h-4 w-4 text-blue-600" />;
      case 'USER':
        return <User className="h-4 w-4 text-purple-600" />;
      case 'APPLICATION':
        return <FileText className="h-4 w-4 text-green-600" />;
      case 'DOCUMENT':
        return <FileText className="h-4 w-4 text-orange-600" />;
      case 'APPOINTMENT':
        return <Calendar className="h-4 w-4 text-pink-600" />;
      case 'SYSTEM':
        return <Settings className="h-4 w-4 text-gray-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return <Badge variant="error" size="sm">Admin</Badge>;
      case 'OPS':
        return <Badge variant="info" size="sm">Operator</Badge>;
      case 'APPLICANT':
        return <Badge variant="success" size="sm">Applicant</Badge>;
      default:
        return <Badge variant="default" size="sm">{role}</Badge>;
    }
  };

  const handleExport = () => {
    console.log('✅ Mock: Export audit logs');
    alert('Would download audit logs as CSV/Excel file');
  };

  // Stats
  const stats = {
    total: logs.length,
    success: logs.filter((l) => l.status === 'SUCCESS').length,
    failures: logs.filter((l) => l.status === 'FAILURE').length,
    warnings: logs.filter((l) => l.status === 'WARNING').length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ncaa-dark">Audit Logs</h1>
            <p className="text-ncaa-medium mt-1">System activity tracking and compliance monitoring</p>
          </div>
          <Button onClick={handleExport} variant="outline" icon={<Download className="h-4 w-4" />}>
            Export Logs
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Events</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Successful</p>
              <p className="text-2xl font-bold text-gray-900">{stats.success}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Failures</p>
              <p className="text-2xl font-bold text-gray-900">{stats.failures}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Warnings</p>
              <p className="text-2xl font-bold text-gray-900">{stats.warnings}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="AUTH">Authentication</option>
            <option value="USER">User Management</option>
            <option value="APPLICATION">Applications</option>
            <option value="DOCUMENT">Documents</option>
            <option value="APPOINTMENT">Appointments</option>
            <option value="SYSTEM">System</option>
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILURE">Failure</option>
            <option value="WARNING">Warning</option>
          </select>

          {/* Role Filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
          >
            <option value="all">All Roles</option>
            <option value="ADMIN">Admin</option>
            <option value="OPS">Operator</option>
            <option value="APPLICANT">Applicant</option>
          </select>
        </div>
      </Card>

      {/* Results Info */}
      {filteredLogs.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
          <p>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredLogs.length)} of{' '}
            {filteredLogs.length} events
          </p>
        </div>
      )}

      {/* Logs List */}
      <div className="space-y-3">
        {paginatedLogs.length === 0 ? (
          <Card>
            <div className="text-center py-16">
              <Activity className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Audit Logs Found</h3>
              <p className="text-gray-600">Try adjusting your search or filters</p>
            </div>
          </Card>
        ) : (
          paginatedLogs.map((log) => (
            <Card key={log.id} className="hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="p-2 bg-gray-50 rounded-lg flex-shrink-0">
                  {getCategoryIcon(log.category)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{log.action.replace(/_/g, ' ')}</h3>
                        {getStatusBadge(log.status)}
                      </div>
                      <p className="text-sm text-gray-700">{log.details}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDateTime(log.timestamp)}
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {log.user_name}
                    </div>
                    {getRoleBadge(log.user_role)}
                    <div className="flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      {log.ip_address}
                    </div>
                    {log.entity_type && log.entity_id && (
                      <Badge variant="default" size="sm">
                        {log.entity_type} #{log.entity_id}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              const showPage =
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1);

              const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
              const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

              if (showEllipsisBefore || showEllipsisAfter) {
                return (
                  <span key={page} className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }

              if (!showPage) return null;

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-ncaa-primary text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};
